<template>
  <EgovPASiteBanner />

  <v-app-bar
    extension-height="0"
    :title1="'Pennsylvania'"
    :title2="'Department of Transportation'"
    style="position: static"
  >
    <!-- Skip link -->

    <a href="#main" ref="skipLink" class="skip-link">Skip to main content</a>

    <!-- Image & Title -->
    <div class="PennDOTKeystoneImgContainer">
      <a class="PennDOTimgLink" :href="dvsonlineLink" target="_blank" rel="noopener noreferrer">
        <img class="PennDOTKeystoneLogo" :src="logo" alt="" role="presentation" />
        <p class="titleBar" aria-hidden="true"></p>
        <div class="titleContainer">
          <p class="titleP">{{ headerProps.title1 }}</p>
          <p class="titleD">{{ headerProps.title2 }}</p>
        </div>
      </a>
    </div>

    <!-- Navigation and Links -->
    <template #append>
      <div class="navigationContainer">
        <!-- DESKTOP: Online Services Menu -->
        <v-menu
          v-if="$vuetify?.display?.mdAndUp"
          v-model="desktopMenuOpen"
          location="bottom"
          :close-on-content-click="false"
          eager
        >
          <template #activator="{ props: activatorProps }">
            <v-btn
              class="dashboardButton"
              variant="flat"
              v-bind="activatorProps"
              :append-icon="desktopMenuOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
            >
              Online Services
            </v-btn>
          </template>

          <v-list class="list" v-model:opened="openedGroups">
            <v-list-group v-for="service in services" :key="service.title" :value="service.title">
              <template #activator="{ props: groupProps }">
                <v-list-item v-bind="groupProps" :title="service.title" />
              </template>

              <v-list-item
                v-for="subItem in service.subItems"
                :key="subItem.title"
                :href="subItem.href"
                :target="subItem.target"
                :rel="subItem.rel"
                :tabindex="0"
                @click="handleSubItemClick(subItem.action)"
                @keydown.enter="handleSubItemKeydown($event, subItem.action)"
                @keydown.space="handleSubItemKeydown($event, subItem.action)"
              >
                <v-list-item-title>{{ subItem.title }}</v-list-item-title>
              </v-list-item>
            </v-list-group>
          </v-list>
        </v-menu>

        <!-- Separate Links for Desktop View -->
        <v-btn
          v-if="$vuetify?.display?.mdAndUp"
          class="linkButton"
          variant="flat"
          :href="publicationsLink"
          target="_blank"
        >
          Forms and Publications
        </v-btn>

        <v-btn
          v-if="$vuetify?.display?.mdAndUp"
          class="linkButton faqButton"
          variant="flat"
          :href="IDLink"
          target="_blank"
          aria-label="Driver Licensing, Photo, and ID FAQs"
        >
          FAQ
        </v-btn>

        <EgovLogoutButton
          v-if="
            $vuetify?.display?.mdAndUp &&
            (userLogin.loginResponses.size > 0 || userLogin.vsLoginResponses.size > 0)
          "
          text1="You are attempting to log out of your current session."
          text2="The services currently in your cart and/or any information you have entered during this session will no longer be available after you log out."
          text3="Select Go Back to finish your session. If you are ready to log out, select the Log Out button below."
        >
          <template #activator="{ onClick }">
            <v-btn
              class="linkButton headerLogoutButton"
              variant="flat"
              append-icon="ri-logout-box-r-line"
              @click="onClick"
            >
              Log out
            </v-btn>
          </template>
        </EgovLogoutButton>

        <EgovIconButton
          v-if="shoppingCartStore.cartItems.length > 0"
          icon="ri-shopping-cart-2-fill"
          size="x-large"
          class="dashboardButtonMenu"
          variant="text"
          @click="handleCartClick"
          showCount
          :itemCount="shoppingCartStore.cartItems.length"
        />

        <!-- MOBILE: Hamburger Menu -->
        <v-menu
          v-if="!$vuetify?.display?.mdAndUp"
          v-model="mobileMenuOpen"
          location="bottom"
          :close-on-content-click="false"
        >
          <template #activator="{ props: activatorProps }">
            <EgovIconButton
              icon="ri-menu-line"
              size="x-large"
              class="dashboardButtonMenu"
              variant="text"
              v-bind="activatorProps"
            />
          </template>

          <v-list class="list" v-model:opened="openedGroups">
            <!-- Online Services group -->
            <v-list-group :value="'Online Services'">
              <template #activator="{ props: groupProps }">
                <v-list-item v-bind="groupProps" title="Online Services" />
              </template>

              <v-list-group v-for="service in services" :key="service.title" :value="service.title" :tabindex="0">
                <template #activator="{ props: serviceProps }">
                  <v-list-item v-bind="serviceProps" :title="service.title" />
                </template>

                <v-list-item
                  v-for="subItem in service.subItems"
                  :key="subItem.title"
                  :href="subItem.href"
                  :target="subItem.target"
                  :rel="subItem.rel"
                  :tabindex="0"
                  @click="handleSubItemClick(subItem.action)"
                  @keydown.enter="handleSubItemKeydown($event, subItem.action)"
                  @keydown.space="handleSubItemKeydown($event, subItem.action)"
                >
                  <v-list-item-title>{{ subItem.title }}</v-list-item-title>
                </v-list-item>
              </v-list-group>
            </v-list-group>

            <!-- Mobile extra links -->
            <v-list-item :href="publicationsLink" target="_blank">
              <v-list-item-title>Forms and Publications</v-list-item-title>
            </v-list-item>

            <v-list-item :href="IDLink" target="_blank">
              <v-list-item-title>FAQ</v-list-item-title>
            </v-list-item>

            <EgovLogoutButton
              v-if="userLogin.loginResponses.size > 0 || userLogin.vsLoginResponses.size > 0"
              text1="You are attempting to log out of your current session."
              text2="The services currently in your cart and/or any information you have entered during this session will no longer be available after you log out."
              text3="Select Go Back to finish your session. If you are ready to log out, select the Log Out button below."
            >
              <template #activator="{ onClick }">
                <v-list-item class="mobile-logout-item" @click="onClick">
                  <v-list-item-title>Log out</v-list-item-title>
                  <template #append>
                    <v-icon class="mobile-logout-icon" icon="ri-logout-box-r-line" size="small" />
                  </template>
                </v-list-item>
              </template>
            </EgovLogoutButton>
          </v-list>
        </v-menu>
      </div>
    </template>
  </v-app-bar>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import logo from '@/assets/PennDOT-keystone-logo.svg'
import EgovPASiteBanner from '@/components/common/EgovPASiteBanner.vue'
import EgovIconButton from '@/components/common/EgovIconButton.vue'
import EgovLogoutButton from '@/components/common/EgovLogoutButton.vue'
import { useShoppingCartStore } from '@/stores/shoppingCart'
import { useUserLogin } from '@/stores/userLogin'
import { useNavigationLinks } from '@/composables/useNavigationLinks'

const router = useRouter()
const shoppingCartStore = useShoppingCartStore()
const userLogin = useUserLogin()

const headerProps = defineProps({
  title1: { type: String, default: 'Pennsylvania' },
  title2: { type: String, default: 'Department of Transportation' },
})

const desktopMenuOpen = ref(false)
const mobileMenuOpen = ref(false)

const { useNavLink } = useNavigationLinks()

const { url: IDLink } = useNavLink('NAVFAQS')
const { url: publicationsLink } = useNavLink('NAVPUBLICATIONS')
const { url: dvsonlineLink } = useNavLink('NAVDVSONLINESVC')
const { url: takeSurveyLink } = useNavLink('TAKEASURVEY')
const { url: contactUsLink } = useNavLink('NAVCONTACTUS')

const openedGroups = ref([])

const closeMenus = () => {
  desktopMenuOpen.value = false
  mobileMenuOpen.value = false
}

watch(() => router.currentRoute.value.fullPath, closeMenus)

const handleCartClick = () => {
  closeMenus()
  router.push({ name: 'ShoppingCart' })
}

const handleSubItemClick = (destination) => {
  if (!destination) return
  closeMenus()
  router.push(typeof destination === 'string' ? { name: destination } : destination)
}

const handleSubItemKeydown = (event, destination) => {
  if (!destination) return
  event.preventDefault()
  handleSubItemClick(destination)
}

// prettier-ignore
const services = computed(() => [
  {
    title: 'DL Services',
    subItems: [ { title: 'Change of Address on Driver License/Photo ID', action: 'DLLogin' }, { title: 'Renewal of Driver License/Photo ID', action: 'DLLogin' }, { title: 'Duplicate of Driver License/Photo ID/Camera Card', action: 'DLLogin' }, { title: 'Add Veterans Donor Designation', action: 'DLLogin' }, { title: 'Add Organ Donor Designation', action: 'OrganDonorLogin' }, { title: 'School Bus Recertification Letter', action: 'DLLogin' }, { title: 'Update Emergency Contact Information', action: 'DLLogin' }, ],
  },
  {
    title: 'Vehicle Services',
    subItems: [ { title: 'Change of Address on Vehicle/Trailer', action: { name: 'VSLogin', query: { q: 'plate' } } }, { title: 'Vehicle/Trailer Registration Renewal', action: { name: 'VSLogin', query: { q: 'plate' } } }, { title: 'Retired Status Rate Registration Renewal', action: { name: 'VSLogin', query: { q: 'plate' } } }, { title: 'Pay County Local Use Fee', action: { name: 'VSLogin', query: { q: 'plate' } } }, { title: 'Duplicate of Vehicle Registration', action: { name: 'VSLogin', query: { q: 'plate' } } }, { title: 'Personalized Plate Availability', action: { name: 'VSLogin', query: { q: 'plate' } } }, ],
  },
  {
    title: 'Exam Scheduling Services',
    subItems: [ { title: 'Schedule a Driver Test Special Points Exam', action: 'ESLogin' }, { title: 'Reschedule a Driver Test or Special Points Exam', action: 'ESLogin' }, { title: 'Cancel a Driver Test or Special Points Exam', action: 'ESLogin' }, ],
  },
  {
    title: "Driver's License ID Restoration Services",
    subItems: [ { title: "Driver's License Restoration Requirements Letter", action: 'DLLogin' }, { title: "Driver's License Pay Restoration Fee", action: 'DLLogin' }, ],
  },
  {
    title: 'Vehicle Restoration Services',
    subItems: [ { title: 'Vehicle Registration Restoration Requirements Letter', action: 'VSLogin' }, { title: 'Pay Vehicle Registration Restoration Fee', action: 'VSLogin' }, { title: 'Pay Civil Penalty Fee in Lieu of Suspension', action: 'VSLogin' }, { title: 'Provide Proof of Insurance', action: 'VSLogin' }, ],
  },
  {
    title: 'Other Services',
    subItems: [ { title: 'Find a Penndot Location', action: 'FindALocation' }, { title: 'Take a Survey', href: takeSurveyLink.value, target: '_blank', rel: 'noopener noreferrer' }, { title: 'Ask a Question', href: contactUsLink.value, target: '_blank', rel: 'noopener noreferrer' }, ],
  },
  {
    title: 'REAL ID Services',
    subItems: [ { title: 'REAL ID Opt-In', action: 'DLLogin' }, { title: 'REAL ID Pre-Verification Enrollment', action: 'PreVerifyLogin' }, ],
  },
])
</script>

<style lang="scss" scoped>
.v-app-bar.v-toolbar:not(.v-toolbar--flat) {
  box-shadow: none;
}

.v-toolbar {
  border-collapse: separate;
  border-bottom: 1px solid var(--kds-secondary-fixed-dim);
  :deep(.v-toolbar__content) {
    overflow: visible;
  }
}

.skip-links {
  list-style: none;
}

a.skip-link {
  white-space: nowrap;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  scale: 0.65;
  background-color: #0056b3;
  color: #ffffff;
  padding: 0.25em;
  border: 2px solid #003f7f;
  border-radius: 4px;
  text-decoration: none;
  &:link,
  &:visited,
  &:hover,
  &:active {
    color: #ffffff;
  }
}

.skip-link:focus {
  opacity: 1;
  scale: 1;
}

.PennDOTKeystoneImgContainer .PennDOTimgLink {
  width: max-content;
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  justify-content: start;
  text-decoration: none;
  transform-origin: center left;

  @media (max-width: 760px) {
    transform: scale(0.75);
  }
  @media (max-width: 540px) {
    transform: scale(0.6);
  }
  @media (max-width: 545px) {
    transform: scale(1);
  }
}

.PennDOTKeystoneLogo {
  width: 100%;
  max-width: 45px;
  margin-right: 4px;
}

.titleContainer {
  color: black;
  line-height: 1.1rem;

  @media (max-width: 565px) {
    display: none;
  }
  @media (max-width: 545px) {
    display: none;
  }
}

.titleBar {
  display: flex;
  border-left: solid 1.25px black;
  height: 45px;
  width: 1px;

  @media (max-width: 565px) {
    display: none;
  }
  @media (max-width: 545px) {
    display: none;
  }
}

.titleP {
  margin-left: 5px;
  font-size: small;
  font-weight: 500;
}

.titleD {
  margin-left: 5px;
  font-size: medium;
  width: max-content;
  font-weight: 700;
}
:deep(.v-toolbar__content) {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}
.navigationContainer {
  display: flex;
  align-items: center;
  height: 100%;
  width: auto;
  gap: 4px;
  text-transform: none;
}
:deep(.v-list) {
  max-height: calc(100vh - 180px);
}
.list.v-list {
  border-radius: 0px 0px 10px 10px;

  :deep(.v-icon .v-icon__svg) {
    height: 25px;
    color: var(--kds-on-surface);
  }

  :deep(.v-list-item .v-list-item-title) {
    font-weight: 600;
    padding: 0px 10px;
    color: var(--kds-on-surface);

    @media (max-width: 565px) {
      font-size: 0.875rem;
    }
  }

  :deep(.mobile-logout-icon) {
    color: var(--kds-on-surface);
  }

  :deep(.mobile-logout-icon .v-icon__svg) {
    height: 20px;
  }
}

.dashboardNavigationDropDown {
  @media (max-height: 420px) {
    max-height: 150px;
  }
}

.dashboardButton {
  color: var(--kds-on-surface);
  font-weight: 600;
  text-transform: none;
  width: auto;
  border-radius: 0;
  min-height: 100%;
  box-shadow: none;

  @media (max-width: 565px) {
    padding-left: 10px;
  }
}

.navigationContainer .dashboardButtonMenu {
  text-transform: none;
  width: 60px;
  border-radius: 0;
  min-height: 100%;
  box-shadow: none;
}

.linkButton {
  color: var(--kds-on-surface);
  text-transform: none;
  width: auto;
  border-radius: 0;
  min-height: 100%;
  box-shadow: none;

  @media (max-width: 565px) {
    padding-left: 10px;
    font-weight: 900;
  }
}
</style>
